import { describeError, useApi } from '@classgo/app-kit';
import type { ReportCardBatchView, StudentListItem } from '@classgo/shared';
import { Badge, Button, Card, EmptyState, Modal, Select } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { listQuery } from '../../academic/api';
import { refusalOf } from '../../attendance/api';
import { useT } from '../../i18n';
import { recordQueries } from '../../records/api';
import { reportCardQueries, reportCardsApi } from '../../report-cards/api';
import { collectAll, fetchBytes, saveFile, zipFilename, zipOf } from '../../report-cards/download';
import type { DownloadFailure } from '../../report-cards/types';
import { can } from '../../shell';
import { useTenant } from '../../tenant/tenant-provider';

type DownloadAll =
  | { phase: 'running'; done: number; total: number }
  | { phase: 'done'; count: number; failures: DownloadFailure[] };

const fullName = (row: { givenName: string | null; familyName: string | null }) =>
  [row.givenName, row.familyName].filter(Boolean).join(' ');

const BATCH_TONE: Record<ReportCardBatchView['status'], 'gray' | 'info' | 'success' | 'danger'> = {
  queued: 'gray',
  running: 'info',
  done: 'success',
  failed: 'danger',
};

/**
 * Boletines (#116 over the API of #107): a group of the sede and a period of the active year;
 * with `grades:publish`, "Generar boletines" starts the batch behind a confirmation that says how
 * many students and that the families are notified, and the screen follows it (polled every two
 * seconds) until it is done, showing generated, failed and the named errors. Every student has
 * "Descargar PDF" (the API's five-minute link, fetched and saved under the API's file name) and
 * "Descargar todos (ZIP)" fetches them all, four at a time, and zips them in the browser, naming
 * the students that failed (no published grades) instead of stopping.
 */
export const ReportCardsScreen = () => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const { session, sede } = useTenant();
  const canPublish = can(session, 'grades:publish');
  const groups = useQuery(listQuery(api, 'groups', { sedeId: sede.id }));
  const years = useQuery(listQuery(api, 'years'));
  const activeYear =
    years.data?.items.find((year) => year.status === 'active') ?? years.data?.items[0];
  const periods = useQuery({
    ...listQuery(api, 'periods', { schoolYearId: activeYear?.id ?? '' }),
    enabled: !!activeYear,
  });
  const [groupId, setGroupId] = useState('');
  const [periodId, setPeriodId] = useState('');
  useEffect(() => {
    if (!groupId && groups.data?.items[0]) setGroupId(groups.data.items[0].id);
    if (!periodId && periods.data?.items[0]) setPeriodId(periods.data.items[0].id);
  }, [groupId, periodId, groups.data, periods.data]);
  const students = useQuery({
    // A group is far under the API's page of 200: one read lists everyone the ZIP walks.
    ...recordQueries.students(api, { sedeId: sede.id, groupId, pageSize: 200 }),
    enabled: groupId !== '',
  });
  const rows = (students.data?.items ?? []).filter((row) => row.status === 'active');
  const names = {
    group: groups.data?.items.find((item) => item.id === groupId)?.name ?? '',
    period: periods.data?.items.find((item) => item.id === periodId)?.name ?? '',
  };

  // The batch belongs to the cell it was asked for: another group or period hides it.
  const cell = `${groupId}|${periodId}`;
  const [started, setStarted] = useState<{ cell: string; id: string } | null>(null);
  const batch = useQuery(reportCardQueries.batch(api, started?.cell === cell ? started.id : ''));
  const [confirming, setConfirming] = useState(false);
  const generate = useMutation({
    mutationFn: () => reportCardsApi(api).generate({ groupId, periodId }),
    onSuccess: (created) => {
      queries.setQueryData(reportCardQueries.batch(api, created.id).queryKey, created);
      setStarted({ cell, id: created.id });
      setConfirming(false);
    },
  });
  const askToGenerate = () => {
    generate.reset();
    setConfirming(true);
  };
  const shown = started?.cell === cell && batch.data !== undefined;

  const messageOf = (thrown: unknown) =>
    refusalOf(thrown) ??
    (thrown instanceof Error && thrown.message.startsWith('HTTP ')
      ? t('reportCards.download.failed')
      : describeError(t, thrown));
  const [busy, setBusy] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const downloadOne = async (row: StudentListItem) => {
    setBusy(row.id);
    setRowErrors((errors) => ({ ...errors, [row.id]: '' }));
    try {
      const link = await reportCardsApi(api).download(row.id, periodId);
      saveFile(await fetchBytes(link.url), link.filename, 'application/pdf');
    } catch (thrown) {
      setRowErrors((errors) => ({ ...errors, [row.id]: messageOf(thrown) }));
    } finally {
      setBusy(null);
    }
  };
  const [all, setAll] = useState<DownloadAll | null>(null);
  const downloadAll = async () => {
    setAll({ phase: 'running', done: 0, total: rows.length });
    const result = await collectAll(
      rows.map((row) => ({ studentId: row.id, studentName: fullName(row) })),
      async (target) => {
        const link = await reportCardsApi(api).download(target.studentId, periodId);
        return { name: link.filename, bytes: await fetchBytes(link.url) };
      },
      { onProgress: (done) => setAll({ phase: 'running', done, total: rows.length }), messageOf },
    );
    if (result.entries.length > 0) {
      saveFile(zipOf(result.entries), zipFilename(names.group, names.period), 'application/zip');
    }
    setAll({ phase: 'done', count: result.entries.length, failures: result.failures });
  };

  const errorOf = (row: StudentListItem) =>
    shown ? batch.data?.errors.find((error) => error.studentId === row.id) : undefined;
  const stateOf = (row: StudentListItem) => {
    const failed = errorOf(row);
    if (failed) return <span className="text-danger-700">{failed.message}</span>;
    if (shown && batch.data?.status === 'done') return t('reportCards.state.generated');
    return '—';
  };

  return (
    <Card title={t('reportCards.title')}>
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <Select
          label={t('reportCards.filter.group')}
          value={groupId}
          onChange={(event) => setGroupId(event.target.value)}
          options={(groups.data?.items ?? []).map((item) => ({
            value: item.id,
            label: item.name,
          }))}
        />
        <Select
          label={t('reportCards.filter.period')}
          value={periodId}
          onChange={(event) => setPeriodId(event.target.value)}
          options={(periods.data?.items ?? []).map((period) => ({
            value: period.id,
            label: period.name,
          }))}
        />
      </div>
      {students.isError ? (
        <EmptyState
          title={t('entity.error.title')}
          description={describeError(t, students.error)}
        />
      ) : !students.data ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-600">
              {t('reportCards.students', { count: rows.length, group: names.group })}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="secondary"
                disabled={rows.length === 0 || busy !== null}
                loading={all?.phase === 'running'}
                onClick={() => void downloadAll()}
              >
                {t('reportCards.downloadAll')}
              </Button>
              {canPublish ? (
                <Button disabled={rows.length === 0} onClick={askToGenerate}>
                  {t('reportCards.generate')}
                </Button>
              ) : null}
            </div>
          </div>
          {shown && batch.data ? <BatchPanel batch={batch.data} /> : null}
          {all?.phase === 'running' ? (
            <p role="status" className="text-sm text-gray-600">
              {t('reportCards.downloadAll.progress', { done: all.done, total: all.total })}
            </p>
          ) : all?.phase === 'done' ? (
            <div
              role="status"
              className={`rounded-control px-3 py-2 text-sm ${
                all.count === 0
                  ? 'bg-danger-100 text-danger-700'
                  : 'bg-success-100 text-success-700'
              }`}
            >
              <p>
                {all.count === 0
                  ? t('reportCards.downloadAll.none')
                  : all.failures.length === 0
                    ? t('reportCards.downloadAll.done', { count: all.count })
                    : t('reportCards.downloadAll.partial', {
                        count: all.count,
                        failed: all.failures.length,
                      })}
              </p>
              {all.failures.length > 0 ? (
                <ul aria-label={t('reportCards.downloadAll.failures')} className="mt-1 space-y-1">
                  {all.failures.map((failure) => (
                    <li key={failure.studentId}>
                      <span className="font-medium">{failure.studentName}:</span> {failure.message}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          {rows.length === 0 ? (
            <p className="text-sm text-gray-500">{t('reportCards.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" aria-label={t('reportCards.caption')}>
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="pb-2 pr-3 font-semibold">{t('reportCards.column.student')}</th>
                    <th className="pb-2 pr-3 font-semibold">{t('reportCards.column.state')}</th>
                    <th className="pb-2 font-semibold">
                      <span className="sr-only">{t('reportCards.column.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t border-gray-100 align-middle">
                      <td className="py-2 pr-3 font-medium text-gray-900">{fullName(row)}</td>
                      <td className="py-2 pr-3 text-gray-600">
                        {stateOf(row)}
                        {rowErrors[row.id] ? (
                          <p role="alert" className="text-danger-700">
                            {rowErrors[row.id]}
                          </p>
                        ) : null}
                      </td>
                      <td className="py-2 text-right">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="whitespace-nowrap"
                          aria-label={t('reportCards.download.aria', { name: fullName(row) })}
                          loading={busy === row.id}
                          disabled={(busy !== null && busy !== row.id) || all?.phase === 'running'}
                          onClick={() => void downloadOne(row)}
                        >
                          {t('reportCards.download')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={t('reportCards.generate.title', names)}
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              {t('common.cancel')}
            </Button>
            <Button loading={generate.isPending} onClick={() => generate.mutate()}>
              {t('reportCards.generate.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            {t('reportCards.generate.body', { count: rows.length, period: names.period })}
          </p>
          {generate.isError ? (
            <p
              role="alert"
              className="rounded-control bg-danger-100 px-3 py-2 text-sm text-danger-700"
            >
              {t('reportCards.generate.failed')}{' '}
              {refusalOf(generate.error) ?? describeError(t, generate.error)}
            </p>
          ) : null}
        </div>
      </Modal>
    </Card>
  );
};

/**
 * The batch as it moves: its state, how many are generated and failed of the total, a bar, and
 * once it stops, the students whose boletín failed with the reason (or the batch's own error).
 */
const BatchPanel = ({ batch }: { batch: ReportCardBatchView }) => {
  const t = useT();
  const finished = batch.generated + batch.failed;
  const percent = batch.total === 0 ? 100 : Math.round((finished / batch.total) * 100);
  return (
    <section
      aria-label={t('reportCards.batch.title', { period: batch.periodName })}
      className="space-y-2 rounded-control border border-gray-200 p-3"
    >
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <Badge tone={BATCH_TONE[batch.status]}>
          {t(`reportCards.batch.status.${batch.status}`)}
        </Badge>
        <span role="status">
          {t('reportCards.batch.progress', {
            generated: batch.generated,
            failed: batch.failed,
            total: batch.total,
          })}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={t('reportCards.batch.title', { period: batch.periodName })}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
      >
        <div className="h-full bg-brand-600 transition-all" style={{ width: `${percent}%` }} />
      </div>
      {batch.error ? (
        <p role="alert" className="text-sm text-danger-700">
          {t('reportCards.batch.error', { message: batch.error })}
        </p>
      ) : null}
      {batch.errors.length > 0 ? (
        <div className="text-sm">
          <p className="font-medium text-gray-900">{t('reportCards.batch.errors')}</p>
          <ul aria-label={t('reportCards.batch.errors')} className="mt-1 space-y-1 text-gray-700">
            {batch.errors.map((error) => (
              <li key={error.studentId}>
                <span className="font-medium">{error.studentName}:</span> {error.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
};
